import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import BarcodeScanner from '../components/BarcodeScanner';
import Cart from '../components/Cart';
import Checkout from '../components/Checkout';
import RegisterManager from '../components/RegisterManager';
import api from '../services/api';
import { ShiftContext } from '../contexts/ShiftContext';
import { SocketContext } from '../contexts/SocketContext';
import RegisterShift, { CloseShiftModal } from '../components/RegisterShift';
import { OfflineContext } from '../contexts/OfflineContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { Search, ScanLine, ShoppingCart, ArrowUpRight, ArrowDownRight, Trash2, CloudOff, RefreshCw, Wifi, Mic, Package, Sparkles, X } from 'lucide-react';
import { db, saveProductsOffline, queueSaleOffline } from '../services/offline-db';

export default function POS() {
  const [session, setSession] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [globalStockItem, setGlobalStockItem] = useState(null);
  const [globalStockData, setGlobalStockData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedProductForVariants, setSelectedProductForVariants] = useState(null);
  const { settings, currencySymbol } = useSettings();
  const { online, syncing, syncPendingSales } = useContext(OfflineContext);
  const { theme } = useContext(ThemeContext);
  const socket = useContext(SocketContext);
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [appliedPromos, setAppliedPromos] = useState([]);
  const [customerDisplayChannel] = useState(new BroadcastChannel('customer_display'));

  const taxRatePercent = parseFloat(settings.tax_rate) || 0;

  // --- Voice Search (Web Speech API) ---
  const [isListening, setIsListening] = useState(false);
  const startVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice search is not supported in this browser.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchTerm(transcript.replace(/\.$/, '')); // Remove trailing period
    };
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };
    recognition.onend = () => setIsListening(false);
    
    recognition.start();
  };

  // --- Keyboard Shortcuts ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input (except for F-keys which we intercept)
      const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
      
      switch(e.key) {
        case 'F1':
          e.preventDefault();
          document.getElementById('pos-search-input')?.focus();
          break;
        case 'F2':
          e.preventDefault();
          if (cart.length > 0) setShowCheckout(true);
          break;
        case 'F4':
          e.preventDefault();
          if (window.confirm('Clear cart?')) setCart([]);
          break;
        case 'Escape':
          if (showCheckout) setShowCheckout(false);
          if (showScanner) setShowScanner(false);
          if (selectedProductForVariants) setSelectedProductForVariants(null);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart.length, showCheckout, showScanner, selectedProductForVariants]);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products');
      setProducts(res.data);
      await saveProductsOffline(res.data);
    } catch (err) {
      console.error('Product Fetch Error:', err);
      if (!navigator.onLine) {
        const localProds = await db.products.toArray();
        setProducts(localProds);
      }
    }
  };

  useEffect(() => {
    fetchProducts();

    // Fetch Categories
    api.get('/categories')
      .then(res => setCategories([{ id: 'All', name: 'All' }, ...res.data]))
      .catch(async (err) => {
        console.error('Category Fetch Error:', err);
        setCategories([{ id: 'All', name: 'All' }]);
      });
      

      
    // Fetch Promotions
    api.get('/promotions')
      .then(res => setPromotions(res.data.filter(p => p.active)))
      .catch(console.error);
  }, [online]);

  useEffect(() => {
    if (socket) {
      socket.on('stock_update', (data) => {
        console.log('Real-time stock update received:', data);
        fetchProducts();
      });
      return () => socket.off('stock_update');
    }
  }, [socket]);

  const checkGlobalStock = async (product, e) => {
    e.stopPropagation();
    setGlobalStockItem(product);
    try {
      const res = await api.get(`/products/global-stock/${product.sku}`);
      setGlobalStockData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const addToCart = (product, variant = null) => {
    if (product.variants?.length > 0 && !variant) {
      setSelectedProductForVariants(product);
      return;
    }

    setCart(prev => {
      const existing = prev.find(i => i.productId === product.id && i.variantId === (variant?.id || null));
      if (existing) return prev.map(i => i === existing ? {...i, quantity: i.quantity+1} : i);
      const price = product.price + (variant ? variant.priceAdj : 0);
      const name = variant ? `${product.name} (${variant.name})` : product.name;
      return [...prev, { productId: product.id, variantId: variant?.id || null, name, price, quantity: 1 }];
    });
    setSelectedProductForVariants(null);
  };

  const handleBarcode = (code) => {
    const prod = products.find(p => p.sku === code);
    if (prod) {
      addToCart(prod);
      setShowScanner(false);
    }
  };

  function calculatePromotions() {
    let totalDiscount = 0;
    const activePromos = [];
    const now = new Date();
    const currentHour = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const day = now.getDay() || 7;

    promotions.forEach(promo => {
      const { config, type, name } = promo;
      let promoApplied = false;

      if (type === 'HAPPY_HOUR') {
        if (config.days.includes(day) && currentHour >= config.startTime && currentHour <= config.endTime) {
          cart.forEach(item => {
            if (config.productIds.includes(item.productId)) {
              totalDiscount += (item.price * item.quantity) * (config.discount / 100);
              promoApplied = true;
            }
          });
        }
      } else if (type === 'BUNDLE') {
        const hasAll = config.productIds.every(id => cart.find(i => i.productId === id));
        if (hasAll) {
          const bundleItems = cart.filter(i => config.productIds.includes(i.productId));
          const bundleTotal = bundleItems.reduce((s, i) => s + (i.price * i.quantity), 0);
          totalDiscount += bundleTotal * (config.discount / 100);
          promoApplied = true;
        }
      } else if (type === 'BOGO') {
        cart.forEach(item => {
          if (config.productIds.includes(item.productId)) {
            const freeUnits = Math.floor(item.quantity / (config.buyQty + config.getQty)) * config.getQty;
            totalDiscount += freeUnits * item.price;
            if (freeUnits > 0) promoApplied = true;
          }
        });
      }

      if (promoApplied) activePromos.push({ id: promo.id, name });
    });

    return { totalDiscount, activePromos };
  }

  // --- Totals & Tax Calculations ---
  const { totalDiscount, activePromos } = calculatePromotions();
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountRatio = cartTotal > 0 ? totalDiscount / cartTotal : 0;
  const taxAmount = Math.round(cart.reduce((acc, item) => {
    const itemSubtotal = item.price * item.quantity;
    const itemDiscount = itemSubtotal * discountRatio;
    const itemTaxable = itemSubtotal - itemDiscount;
    const product = products.find(p => p.id === item.productId);
    const rate = (product?.category?.taxRate !== null && product?.category?.taxRate !== undefined ? product.category.taxRate : taxRatePercent) / 100;
    return acc + (Math.max(0, itemTaxable) * rate);
  }, 0) * 100) / 100;
  const finalTotal = Math.round(((cartTotal - totalDiscount) + taxAmount) * 100) / 100;

  const finishCheckout = async (paymentMethods, customerId, discountCode, pointsRedeemed) => {
    try {
      const currentDiscountRatio = cartTotal > 0 ? totalDiscount / cartTotal : 0;
      const itemsWithDiscounts = cart.map(item => {
        const itemSubtotal = item.price * item.quantity;
        const itemDiscount = itemSubtotal * currentDiscountRatio;
        return {
          ...item,
          price: item.price, // original price
          appliedDiscount: itemDiscount / item.quantity // discount per unit
        };
      });

      const saleData = { 
        id: (crypto && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15), 
        items: itemsWithDiscounts.map(item => ({
          ...item,
          productId: parseInt(item.productId) || 0,
          quantity: parseFloat(item.quantity) || 0,
          price: parseFloat(item.price) || 0,
          appliedDiscount: parseFloat(item.appliedDiscount) || 0
        })), 
        paymentMethods: paymentMethods.map(pm => ({
          ...pm,
          amount: parseFloat(pm.amount) || 0
        })), 
        customerId: customerId ? (parseInt(customerId) || null) : null, 
        discountCode: discountCode || null, 
        pointsRedeemed: parseInt(pointsRedeemed) || 0,
        tax: parseFloat(taxAmount) || 0,
        total: parseFloat(finalTotal) || 0,
        promoDiscount: parseFloat(totalDiscount) || 0,
        createdAt: new Date().toISOString()
      };

      console.log('Final Sale Data for Server:', saleData);

      if (online) {
        const res = await api.post('/sales', saleData);
        console.log('Server Response:', res.data);
        setCart([]);
        return res.data.id;
      } else {
        await queueSaleOffline(saleData);
        setCart([]);
        return null;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown checkout error';
      throw new Error(errorMsg);
    }
  };





  // Broadcast to Customer Display
  useEffect(() => {
    customerDisplayChannel.postMessage({
      cart,
      total: finalTotal,
      subtotal: cartTotal,
      tax: taxAmount,
      discount: totalDiscount,
      promos: activePromos,
      currency: currencySymbol
    });
  }, [cart, finalTotal, totalDiscount]);
  
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || p.category?.name === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'cart'

  return (
    <div className="h-full flex flex-col relative">
      <RegisterManager onSessionChange={setSession} />
      
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between gap-6 mb-8">
        <div className="flex-1 max-w-xl relative group">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-theme-muted group-focus-within:text-brand transition-colors" size={20} />
          <input 
            id="pos-search-input"
            type="text" 
            placeholder="Search products (F1) or Scan barcode..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-theme-card border-2 border-theme rounded-[2rem] py-5 pl-16 pr-8 text-theme-main focus:ring-4 focus:ring-brand/20 focus:border-brand outline-none transition-all font-bold text-lg shadow-soft"
          />
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex gap-2">
            <button 
              onClick={startVoiceSearch}
              className={`p-3 rounded-2xl transition-all ${isListening ? 'bg-rose-500 text-white animate-pulse' : 'bg-theme-main text-theme-muted hover:text-brand'}`}
              title="Voice Search"
            >
              <Mic size={20} />
            </button>
            <button 
              onClick={() => setShowScanner(true)}
              className="p-3 bg-theme-main text-theme-muted hover:text-brand rounded-2xl transition-all"
              title="Scan Barcode"
            >
              <ScanLine size={20} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="glass px-6 py-4 rounded-3xl flex items-center gap-4 border-brand/20 shadow-lg shadow-brand/10">
            <div className={`w-3 h-3 rounded-full animate-pulse ${session ? 'bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.5)]'}`}></div>
            <div className="flex flex-col">
              <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] leading-none mb-1">System Status</span>
              <span className="text-sm font-black text-theme-main uppercase tracking-tighter leading-none">
                Register {session ? 'Active' : 'Closed'}
              </span>
            </div>
          </div>
          {!session && (
            <button 
              onClick={() => navigate('/app/register')}
              className="bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-brand/30 transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              Open Register
            </button>
          )}
        </div>
      </div>
      
      {/* Mobile Tab Switcher */}
      <div className="lg:hidden flex border-b border-theme mb-4 bg-theme-card rounded-xl overflow-hidden shadow-sm">
        <button 
          onClick={() => setActiveTab('products')}
          className={`flex-1 py-3 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${activeTab === 'products' ? 'bg-brand text-white shadow-inner' : 'text-theme-muted hover:bg-brand/5'}`}
        >
          <Package size={16} /> Products
        </button>
        <button 
          onClick={() => setActiveTab('cart')}
          className={`flex-1 py-3 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all relative ${activeTab === 'cart' ? 'bg-brand text-white shadow-inner' : 'text-theme-muted hover:bg-brand/5'}`}
        >
          <ShoppingCart size={16} /> Cart
          {cart.length > 0 && (
            <span className="absolute top-2 right-1/4 bg-rose-500 text-white text-[8px] px-1.5 py-0.5 rounded-full animate-bounce">
              {cart.reduce((s, i) => s + i.quantity, 0)}
            </span>
          )}
        </button>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-10rem)] lg:h-[calc(100vh-8rem)] animate-fade-in-up">
      {/* Left: Products Grid */}
      <div className={`flex-1 flex flex-col rounded-2xl shadow-soft border border-theme overflow-hidden bg-theme-card ${activeTab !== 'products' ? 'hidden lg:flex' : 'flex'}`}>
        {/* Header & Search */}
        <div className="p-4 border-b border-theme flex flex-col gap-3 bg-theme-main/10">
          <div className="flex gap-3 items-center">
            <div className="flex-1"></div>
            {!online && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 text-amber-600 rounded-xl border border-amber-500/20 whitespace-nowrap">
                <CloudOff size={16} />
                <span className="text-[10px] font-black uppercase tracking-widest">Offline Mode</span>
              </div>
            )}
            {syncing && (
              <div className="flex items-center gap-2 px-3 py-2 bg-brand/10 text-brand rounded-xl border border-brand/20 whitespace-nowrap animate-pulse">
                <RefreshCw size={16} className="animate-spin" />
                <span className="text-[10px] font-black uppercase tracking-widest">Syncing...</span>
              </div>
            )}
            <button 
              onClick={() => window.open('/customer-display', 'pos_customer_display', 'width=1024,height=768')}
              className="p-3 rounded-xl border border-theme bg-theme-card text-theme-muted hover:border-brand hover:text-brand transition-all shadow-sm flex items-center justify-center"
              title="Open Customer Display"
            >
              <ArrowUpRight size={20} />
            </button>
            <button 
              onClick={() => setShowScanner(!showScanner)}
              className={`p-3 rounded-xl border border-theme transition-all shadow-sm flex items-center justify-center ${showScanner ? 'bg-brand text-white' : 'bg-theme-card text-theme-muted hover:border-brand'}`}
              title="Toggle Barcode Scanner"
            >
              <ScanLine size={20} />
            </button>
          </div>

          {/* Category Filter Bar */}
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.name)}
                className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all border border-theme flex items-center gap-2 ${
                  selectedCategory === cat.name 
                    ? 'bg-brand text-white shadow-md transform scale-105' 
                    : 'bg-theme-card text-theme-muted hover:border-brand'
                }`}
              >
                {cat.icon && <span className="text-sm">{cat.icon}</span>}
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Scanner View */}
        {showScanner && <BarcodeScanner onDetected={handleBarcode} onClose={() => setShowScanner(false)} />}

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-4">
            {filteredProducts.map(p => (
              <button 
                key={p.id} 
                onClick={() => addToCart(p)}
                className="bg-theme-card rounded-xl p-4 flex flex-col items-center text-center shadow-soft border border-theme hover:border-brand hover:shadow-brand/20 transition-all group transform hover:-translate-y-1"
              >
                {p.icon ? (
                  <div className="text-4xl mb-3">{p.icon}</div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-brand/10 text-brand flex items-center justify-center font-black text-xl mb-3 group-hover:bg-brand group-hover:text-white transition-colors">
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="font-bold text-theme-main leading-tight mb-1 group-hover:text-brand transition-colors line-clamp-2">{p.name}</div>
                <div className="mt-auto pt-2 font-black text-lg text-brand">{currencySymbol}{p.price.toFixed(2)}</div>
                <div className="text-[10px] font-bold text-theme-muted mt-1">Stock: {p.stock}</div>
                <button 
                  onClick={(e) => checkGlobalStock(p, e)}
                  className="mt-3 text-[10px] font-black uppercase tracking-widest text-brand hover:underline flex items-center gap-1"
                >
                  <Wifi size={12} /> Check Branches
                </button>
              </button>
            ))}
          </div>
          {filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-theme-muted">
              <Search size={48} className="mb-4 opacity-50" />
              <p className="font-medium">No products found</p>
            </div>
          )}
        </div>
      </div>

      {/* Right: Cart Sidebar */}
      <div className={`w-full lg:w-96 flex flex-col rounded-2xl shadow-soft border border-theme overflow-hidden bg-theme-card ${activeTab !== 'cart' ? 'hidden lg:flex' : 'flex'}`}>
        <div className="p-4 border-b border-theme flex justify-between items-center">
          <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Current Order</h3>
          <button 
            onClick={() => setCart([])}
            className="p-2 text-theme-muted hover:text-rose-500 transition-colors"
            title="Clear Cart"
          >
            <Trash2 size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-hidden px-4">
          <Cart cart={cart} setCart={setCart} currencySymbol={currencySymbol} />
        </div>
        
        <div className="p-4 border-t border-theme bg-theme-card z-10">
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-theme-muted font-bold">Subtotal</span>
              <span className="text-lg font-black text-theme-main">{currencySymbol}{cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-theme-muted">
              <span className="text-sm font-bold">Tax ({taxRatePercent}%)</span>
              <span className="text-sm font-bold">{currencySymbol}{taxAmount.toFixed(2)}</span>
            </div>
            {totalDiscount > 0 && (
              <div className="flex flex-col gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl animate-pulse">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black uppercase text-emerald-600 flex items-center gap-1">
                    <Sparkles size={12} /> Promotion Savings
                  </span>
                  <span className="text-sm font-black text-emerald-600">-{currencySymbol}{totalDiscount.toFixed(2)}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {activePromos.map(p => (
                    <span key={p.id} className="text-[8px] font-black bg-emerald-500 text-white px-1.5 py-0.5 rounded uppercase tracking-widest">{p.name}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="pt-3 border-t border-theme flex justify-between items-center">
              <span className="text-xl font-black text-theme-main">Total</span>
              <span className="text-3xl font-black text-brand">{currencySymbol}{finalTotal.toFixed(2)}</span>
            </div>
          </div>
          <button 
            onClick={() => setShowCheckout(true)} 
            disabled={cart.length === 0}
            className="w-full bg-brand hover:bg-brand-light disabled:bg-theme-main disabled:text-theme-muted disabled:cursor-not-allowed text-white text-lg font-black py-5 rounded-2xl shadow-lg transition-all transform active:scale-95"
          >
            Complete Checkout
          </button>
        </div>
      </div>

      {showCheckout && <Checkout cart={cart} total={finalTotal} taxAmount={taxAmount} currencySymbol={currencySymbol} onFinish={finishCheckout} close={() => setShowCheckout(false)} />}
      
      {/* Variant Selection Modal */}
      {selectedProductForVariants && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[70] animate-fade-in-up">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden p-6 border border-theme">
            <h3 className="text-xl font-bold text-theme-main mb-2">Select Option</h3>
            <p className="text-theme-muted mb-6 font-medium">{selectedProductForVariants.name}</p>
            
            <div className="space-y-3">
              {selectedProductForVariants.variants.map(v => (
                <button 
                  key={v.id}
                  onClick={() => addToCart(selectedProductForVariants, v)}
                  className="w-full flex justify-between items-center p-4 bg-theme-main/20 hover:bg-brand/10 border border-theme hover:border-brand rounded-2xl transition-all group"
                >
                  <span className="font-bold text-theme-main group-hover:text-brand">{v.name}</span>
                  <span className="text-brand font-black">+{currencySymbol}{v.priceAdj.toFixed(2)}</span>
                </button>
              ))}
              <button 
                onClick={() => addToCart({ ...selectedProductForVariants, variants: [] })}
                className="w-full flex justify-between items-center p-4 bg-theme-card hover:bg-theme-main border border-theme rounded-2xl transition-all group"
              >
                <span className="font-bold text-theme-muted">Standard / No Variant</span>
                <span className="text-theme-muted font-bold">{currencySymbol}0.00</span>
              </button>
            </div>
            
            <button 
              onClick={() => setSelectedProductForVariants(null)}
              className="w-full mt-6 py-3 text-theme-muted font-bold hover:text-theme-main transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      </div>
      {globalStockItem && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-[60] animate-fade-in p-4">
          <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-theme">
            <div className="p-4 border-b border-theme flex justify-between items-center bg-theme-main/10">
              <h3 className="text-sm font-black text-theme-main uppercase tracking-widest">Global Stock: {globalStockItem.name}</h3>
              <button onClick={() => setGlobalStockItem(null)} className="text-theme-muted hover:text-theme-main">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
              {globalStockData.map((s, i) => (
                <div key={i} className={`p-3 rounded-xl border ${s.current ? 'bg-brand/10 border-brand/20' : 'bg-theme-main border-theme'} flex justify-between items-center`}>
                  <div>
                    <div className="text-xs font-black text-theme-main uppercase">{s.storeName}</div>
                    <div className="text-[10px] text-theme-muted">{s.location || 'Main Street'}</div>
                  </div>
                  <div className={`text-sm font-black ${s.stock > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {s.stock} Unit{s.stock !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
              {globalStockData.length === 0 && <p className="text-center py-4 text-theme-muted italic text-xs">No data available for this SKU.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}